import type { OfferItem } from '../types';
import { getOfferExpiryMs } from '../types';
import { todayInZone } from '../../../../utils/eventDate';
import { MIN_OFFER_PAYMENT_AMOUNT } from './paymentLinks';

/**
 * Calendar date (YYYY-MM-DD) for an offer date value. Offer dates arrive as
 * display labels ("Oct 8, 2026") which `new Date` parses as *local* midnight,
 * so the calendar parts must be read locally — converting through UTC shifts
 * the date back a day for anyone east of UTC.
 */
export function toCalendarDate(value?: string | null): string {
  if (!value) return '';
  const iso = value.match(/^(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * First reason a saved offer can't be published as-is, or null. Mirrors the
 * date rules enforced by the create/edit form so a draft sent straight from
 * the details page gets the same guarantees.
 */
export function getOfferPublishBlocker(
  offer: Pick<OfferItem, 'expirationDate' | 'startDate' | 'deliveryDate' | 'milestones' | 'price'>,
  nowMs: number = Date.now(),
): string | null {
  const today = todayInZone(undefined, new Date(nowMs));
  const start = toCalendarDate(offer.startDate);
  const delivery = toCalendarDate(offer.deliveryDate);

  if (!(offer.price > 0)) return 'Please complete all required offer details.';
  if (!start || !delivery) return 'Please complete all required offer details.';
  if (start < today) return 'Start date cannot be in the past.';
  if (delivery < start) return 'Delivery date must be on or after the start date.';

  const expiryMs = getOfferExpiryMs(offer);
  if (expiryMs == null) return 'Please select an offer expiration date and time.';
  if (expiryMs <= nowMs) return 'Offer expiration must be set to a future date and time.';

  const milestoneDates = offer.milestones.map((m) => toCalendarDate(m.dueDate)).filter(Boolean);
  if (milestoneDates.some((d) => d < today)) return 'Milestone due dates cannot be in the past.';

  if (offer.price < MIN_OFFER_PAYMENT_AMOUNT) return MIN_OFFER_AMOUNT_MESSAGE;
  if (offer.milestones.length > 0) {
    if (offer.milestones.some((m) => !(m.amount >= MIN_OFFER_PAYMENT_AMOUNT))) {
      return MIN_MILESTONE_AMOUNT_MESSAGE;
    }
    const total = offer.milestones.reduce((sum, m) => sum + (m.amount || 0), 0);
    if (Math.abs(total - offer.price) > 0.01) return MILESTONE_TOTAL_MESSAGE;
  }

  return null;
}

export const MIN_OFFER_AMOUNT_MESSAGE = `The offer amount must be at least $${MIN_OFFER_PAYMENT_AMOUNT.toFixed(2)}.`;
export const MIN_MILESTONE_AMOUNT_MESSAGE = `Each milestone payment must be at least $${MIN_OFFER_PAYMENT_AMOUNT.toFixed(2)}.`;
export const MILESTONE_TOTAL_MESSAGE =
  'The milestone payment amounts must equal the total offer amount.';

type MilestoneDraft = { percentage: number; amount: number };

/**
 * Resolves each milestone's amount for submission. Amounts derived from
 * percentages are rounded to cents, so when the percentages total 100% the
 * rounding remainder goes on the last milestone to make the sum exact.
 */
export function reconcileMilestoneAmounts<T extends MilestoneDraft>(
  milestones: T[],
  price: number,
): T[] {
  const round = (n: number) => Math.round(n * 100) / 100;
  const resolved = milestones.map((m) => ({
    ...m,
    amount: round(m.amount > 0 ? m.amount : (price * (m.percentage || 0)) / 100),
  }));
  const totalPct = milestones.reduce((sum, m) => sum + (m.percentage || 0), 0);
  if (resolved.length === 0 || price <= 0 || Math.abs(totalPct - 100) > 0.01) return resolved;
  const othersTotal = resolved.slice(0, -1).reduce((sum, m) => sum + m.amount, 0);
  const last = resolved[resolved.length - 1];
  resolved[resolved.length - 1] = { ...last, amount: round(price - othersTotal) };
  return resolved;
}
